import type { BadgeType } from '@deepdive-ui';
import { postedLabel } from './posted';
import { REFERENCE_DATE } from './reference';
import type { RowId } from './sentimentOverrides';

/**
 * Custom reports — Figma page `Custom Reports` (990:44858).
 *
 * A report is a **name and an ordered list of widgets**, and nothing else. It
 * holds no rows, no date range and no chip selections: those come from the
 * dashboard store exactly as they do on every other module, which is why the
 * report frame (994:5153) draws the same date pickers and the same filter bar
 * as Brand Health does. A report says *which* cards, never *what data*.
 *
 * The widgets are identified, not embedded. `WidgetId` names a card that lives
 * in `views/cards/`, and `views/reports/widgets.tsx` maps the name to the
 * component — so a report is plain serialisable data and adding one to a report
 * cannot fork the figure from the module it came from. That indirection is the
 * whole point of the module: "add any widget from other modules" only means
 * anything if it is the same widget.
 */

/**
 * Every card a report can hold.
 *
 * The four `-figure` ids are the `Size=sm` tiles and the rest are the `Size=md`
 * chart cards. `share-of-voice` appears twice on purpose — once as a figure and
 * once as a donut — because Overview draws both and they are different widgets
 * that happen to report the same number.
 *
 * **Every card and table in the app is here** (2026-09-20, at the user's
 * request — the union held thirteen before, which was only what Overview and
 * the Executive summary frame happened to draw). `views/reports/widgets.tsx`
 * maps each to the component its own module renders, so adding one to a report
 * is naming a widget rather than rebuilding it.
 *
 * Two deliberate absences. **Overview's four figures** are listed here, because
 * a new report opens with them, but `Add a widget` filters them out: Overview
 * is a summary of the other modules and its widgets are theirs. **Influencers'
 * per-creator cards** are not here at all — `Platform analytics`, `Audience
 * reactions`, `Brand safety score`, `Industry specific split` and `Sentiment
 * breakdown` are each about one creator, and a report has no way to say which.
 * Only the roster table travels.
 */
export type WidgetId =
  /* Overview — the four `Size=sm` figures. Not offered by `Add a widget`. */
  | 'total-mentions'
  | 'share-of-voice-figure'
  | 'total-unique-audience'
  | 'negative-conversation'
  /* Brand health */
  | 'nps'
  | 'seeded-vs-organic'
  | 'sentiment-over-time'
  | 'engagement-volume'
  | 'engagement-by-type'
  | 'engagement-growth'
  | 'topic-sentiment-split'
  /* Community */
  | 'activity-by-day-and-hour'
  | 'community-topics'
  | 'complaints-by-division'
  | 'top-communities-by-mention'
  | 'community-activity-timeline'
  | 'audience-age-and-gender'
  | 'top-individual-voices'
  | 'top-communities-by-sentiment'
  /* Competitors */
  | 'share-of-voice'
  | 'competitor-sentiment-comparison'
  | 'share-of-positive'
  | 'share-of-voice-over-time'
  | 'competitor-virality'
  | 'mention-volume-vs-competitors'
  | 'competitive-page-performance'
  | 'top-performing-content'
  /* Media */
  | 'media-total-mentions'
  | 'media-positive-mentions'
  | 'media-negative-mentions'
  | 'top-media-outlets'
  | 'media-sentiment-over-time'
  /* Mentions */
  | 'mention-volume-over-time'
  | 'mentions-by-platform'
  | 'intent-based-mentions'
  /* Trends */
  | 'trending-keywords'
  | 'keyword-mentions-frequency'
  | 'top-hashtags'
  | 'keyword-sentiment'
  | 'engagement-spikes'
  | 'trending-topics'
  /* Influencers — the roster only; every other card there needs a creator. */
  | 'influencer-analytics';

/**
 * One widget on a report: which widget, and a key that survives reordering.
 *
 * **No width.** A report is an ordered list and `views/reports/layout.ts` turns
 * it into rows — KPIs across the top, `full` widgets alone, `half` widgets in
 * pairs. Widths travelled here until 2026-09-23, which let a drop leave a row
 * that did not add up to 12; the user's screenshot of a stretched KPI beside a
 * hole is what that looked like.
 */
export type ReportWidget = {
  key: string;
  widget: WidgetId;
};

export type Report = {
  id: string;
  /** Figma `Title#2001:3`. */
  name: string;
  /** Epoch ms. Rendered by `createdLabel`. */
  createdAt: number;
  /**
   * Epoch ms of the last edit — the `Last modified` column the table gained on
   * 2026-09-24. Everything that writes to a report bumps it: a widget added,
   * removed or moved, and a campaign saved. A report that has never been
   * touched carries its own `createdAt`, so the column is never blank.
   *
   * Rendered by `modifiedLabel`, which is the date alone. Figma fills the cell
   * with the `Date created` string ("Created on Sep 18, 2026 at 12:01 am") and
   * the column is 118 wide, so the frame shows it cut off mid-word — the text
   * is copied from its neighbour rather than chosen. See `CustomReportsView`.
   */
  modifiedAt: number;
  /** Figma's second badge — "Created by John Doe". */
  author: string;
  /**
   * That badge's `Type`, and since 2026-09-26 `gray` for everyone.
   *
   * The file draws `Info`, `Positive` and `Neutral` across four tiles with no
   * rule tying a colour to a person, so it was stored rather than derived —
   * inventing a hash from the name would have been inventing a rule the design
   * does not state. In the app the four colours read as a status the author
   * does not have: a green name next to a blue one says something about the two
   * people, and there is nothing to say. `gray` is `Badge`'s own neutral, which
   * is what a name is. The field stays, because a future rule (the signed-in
   * user, a role) would live in it. **Worth fixing in the file.**
   */
  authorBadge: BadgeType;
  widgets: readonly ReportWidget[];
  /**
   * The campaign this report tracks, once one has been configured — Figma
   * `Configure campaigns` (2068:29797, 2068:31496). Absent until then.
   */
  campaign?: Campaign;
};

/**
 * A campaign, as `Configure campaigns` saves it: the keywords that define it,
 * whether it was narrowed to official page posts, and which of the posts that
 * search found the user ticked as belonging to it.
 *
 * **Saving records it; it does not move a figure** (settled with the user
 * 2026-09-20: "the campaign data with that keyword also gets added to the
 * database"). In the product, registering a campaign's keywords is what brings
 * their posts into the tracked data. In this prototype every mention is already
 * in the corpus, so there is nothing further to ingest — the record is the
 * whole of the effect, and the report's widgets read exactly what they read
 * before. Scoping widgets to a campaign is a separate, undesigned step.
 *
 * `postIds` are corpus identities (`rowId`), not positions in the result list,
 * so a saved selection survives a re-sort, a different page, and the date range
 * moving under it.
 */
export type Campaign = {
  /** As typed, in the order typed. */
  keywords: readonly string[];
  /** Figma `Official page posts only`. See `isOfficialPost`. */
  officialOnly: boolean;
  postIds: readonly RowId[];
  /**
   * Figma `Add manually` (2161:35088) — posts named by URL rather than found by
   * keyword, one per line in the frame's textarea.
   *
   * Kept beside `postIds` rather than resolved into them: a `RowId` points at a
   * row of the corpus, and a pasted URL has nothing to point at — the corpus
   * carries no permalink. So a manual campaign records what the user named, and
   * the results table still speaks only for what the keyword search found.
   */
  urls?: readonly string[];
};

/**
 * Figma's exact string — "Created on Sep 18, 2026 at 12:01 am".
 *
 * Built explicitly rather than with `toLocaleString`, which gives "Sep 18, 2026,
 * 12:01 AM": the wrong separator and the wrong case, and both depend on the
 * runtime's locale data, which would make the rendered tile differ between two
 * machines running the same prototype.
 */
const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const;

/**
 * `Mon Sep 14, 9:42 PM` — the one format every table cell holding a specific
 * moment writes, from `data/posted.ts`. Asked for across the app on 2026-09-23.
 *
 * Figma writes this cell "Created on Sep 18, 2026 at 12:01 am" and the one
 * beside it as a bare date; both are replaced. What is lost is the year, which
 * neither column can show under the one format and which no report in the
 * prototype is old enough to need.
 */
export const createdLabel = postedLabel;

/**
 * The same format for `Last modified`.
 *
 * It no longer fits: the column is 118 wide in the frame and this string wants
 * about 150, so it draws truncated where the bare date it replaced fitted. One
 * format across every table was the explicit ask, and the column wants widening
 * in Figma rather than a second format here.
 */
export const modifiedLabel = postedLabel;

/** Figma's "10 widgets added", pluralised — the file only ever draws the plural. */
export const widgetsLabel = (count: number) =>
  `${count} widget${count === 1 ? '' : 's'} added`;

/**
 * Report ids are slugs so a URL reads `#custom-reports/executive-summary`
 * rather than `#custom-reports/3`.
 */
const slug = (name: string) =>
  name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

/**
 * The slug for a new report, made unique against the ones that exist.
 *
 * Names may repeat — two people can each want a report called "Weekly" — but
 * ids cannot, or two tiles would open the same report. So a clash gets `-2`,
 * `-3`, … on the id and the name is left exactly as typed.
 *
 * A name with no letters or digits in it (`***`) slugs to nothing, and falls
 * back to `report` so the URL still has something in it.
 */
export function uniqueReportId(name: string, existing: readonly Report[]): string {
  const base = slug(name) || 'report';
  const taken = new Set(existing.map((report) => report.id));
  if (!taken.has(base)) return base;
  let n = 2;
  while (taken.has(`${base}-${n}`)) n += 1;
  return `${base}-${n}`;
}

/**
 * A widget key no other widget on the report holds.
 *
 * The seeds use `w1`…`w10`, so this continues that series rather than minting a
 * random id: `w11` is legible in a React key and in a drag log, where a uuid is
 * not. It is the highest `wN` plus one and never a count, because a report that
 * has had widgets removed has gaps — counting would re-issue a key that a
 * surviving widget still holds.
 */
export function nextWidgetKey(widgets: readonly ReportWidget[]): string {
  let highest = 0;
  for (const widget of widgets) {
    const match = /^w(\d+)$/.exec(widget.key);
    if (match) highest = Math.max(highest, Number(match[1]));
  }
  return `w${highest + 1}`;
}

/**
 * The prototype's "now" for a thing the user just did: the reference **day**,
 * at the wall clock's **time**.
 *
 * The day is `REFERENCE_DATE`'s because that is the app's today — every date
 * picker, preset and figure is anchored to it, and a report stamped with the
 * real date would claim to have been made after the last day of data it shows.
 * The time is the clock's so that two reports made in one session are told
 * apart and ordered. This reads the clock, which `reference.ts` otherwise
 * forbids — but only for a label on something the user created, never on load
 * and never for a figure, so two reloads still draw identical pages.
 */
export function prototypeNow(): number {
  // eslint-disable-next-line no-restricted-syntax -- time-of-day only, for ordering reports created this session (see above)
  const now = new Date();
  const d = new Date(REFERENCE_DATE);
  d.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), 0);
  return d.getTime();
}

/**
 * Figma's pre-filled name in `Create a report` (2068:28720) — `report_sep_20`,
 * the day the frame was drawn. Same pattern here, on the prototype's today.
 *
 * De-duplicated the way a file manager does it, `report_sep_15_2`, so a second
 * report made the same day does not open with a name that is already taken.
 */
export function defaultReportName(existing: readonly Report[], at: number = REFERENCE_DATE.getTime()): string {
  const d = new Date(at);
  const base = `report_${MONTHS[d.getMonth()].toLowerCase()}_${d.getDate()}`;
  const names = new Set(existing.map((report) => report.name));
  if (!names.has(base)) return base;
  let n = 2;
  while (names.has(`${base}_${n}`)) n += 1;
  return `${base}_${n}`;
}

/**
 * Who is creating reports. The prototype has no sign-in, so there is no
 * identity to read.
 *
 * John Doe because the Figma list draws him on two of four tiles, the most of
 * anyone, and on the Executive summary — the report the detail frame opens. His
 * badge colour is `gray`, as every author's now is — see `authorBadge`. A
 * stand-in, flagged, for
 * whatever the signed-in user turns out to be.
 */
export const CURRENT_USER = { name: 'John Doe', badge: 'gray' } as const satisfies {
  name: string;
  badge: BadgeType;
};

/**
 * What a new report starts with — Figma `New report(Empty)` (2068:29124).
 *
 * The frame's name says empty and its canvas does not: four `Size=sm` figures
 * across the top and `Activity by day and hour` at 788 beneath them. Settled
 * with the user 2026-09-20 — **a new report is never empty**, it opens on these
 * five, so there is no empty state to build. They are the first five widgets of
 * the Executive summary, which is what the frame draws. The widths are the
 * layout's: four KPIs at 283 across the top, then `Activity by day and hour`
 * alone on the next row at 1192 (the frame draws it at 788 beside a gap, which
 * is the shape the 2026-09-23 rules exist to rule out).
 */
export const NEW_REPORT_WIDGETS: readonly ReportWidget[] = [
  { key: 'w1', widget: 'total-mentions' },
  { key: 'w2', widget: 'share-of-voice-figure' },
  { key: 'w3', widget: 'total-unique-audience' },
  { key: 'w4', widget: 'negative-conversation' },
  { key: 'w5', widget: 'activity-by-day-and-hour' },
];

/** Days before the prototype's "now", at the 12:01 am the file draws. */
function createdDaysAgo(days: number): number {
  const d = new Date(REFERENCE_DATE);
  d.setDate(d.getDate() - days);
  d.setHours(0, 1, 0, 0);
  return d.getTime();
}

/**
 * The Executive summary's ten widgets, in the order 994:5153 draws them.
 *
 * The frame's widths were 3/3/3/3, then 8/4, then 4/4/4, then 12 — five
 * different spans, each stored on its widget. Since 2026-09-23 a report stores
 * no widths at all and `views/reports/layout.ts` derives them from this order:
 * the four figures across the top at 283, then pairs at 586, then a lone half
 * and the table at 1192. The frame's own second row (788 beside 384) is the
 * shape the new rules do not allow, because it is a row that only adds up by
 * accident — which is how a drop could leave a hole in it.
 */
const EXECUTIVE_SUMMARY: readonly ReportWidget[] = [
  { key: 'w1', widget: 'total-mentions' },
  { key: 'w2', widget: 'share-of-voice-figure' },
  { key: 'w3', widget: 'total-unique-audience' },
  { key: 'w4', widget: 'negative-conversation' },
  { key: 'w5', widget: 'activity-by-day-and-hour' },
  { key: 'w6', widget: 'nps' },
  { key: 'w7', widget: 'share-of-voice' },
  { key: 'w8', widget: 'complaints-by-division' },
  { key: 'w9', widget: 'trending-keywords' },
  { key: 'w10', widget: 'mention-volume-over-time' },
];

/**
 * The seeded reports: four tiles on the old list frame (1000:38400), rows of
 * the table on the new one (2118:78339).
 *
 * **Figma names all four "Executive summary"**, which is placeholder repetition
 * rather than a claim that a user would make four identically-named reports —
 * the same reading `docs/rules.md` §1 applies to a KPI's value. The first keeps
 * the name, because it is the one the detail frame (994:5153) actually draws;
 * the other three are named for what they would plausibly be, so the list
 * screen shows four distinguishable rows and the search field has something to
 * search. Flagged for the designer in plan.md §7.
 *
 * Everything else is the file's: the authors and their badge colours in the
 * order the tiles draw them, and the 12:01 am timestamp.
 *
 * The three non-executive reports carry cuts of the same ten widgets rather than
 * ten each, so the tiles show different counts and the grid is exercised at
 * more than one length.
 */
export const reports: readonly Report[] = [
  {
    id: slug('Executive summary'),
    name: 'Executive summary',
    createdAt: createdDaysAgo(0),
    modifiedAt: createdDaysAgo(0),
    author: 'John Doe',
    authorBadge: 'gray',
    widgets: EXECUTIVE_SUMMARY,
  },
  {
    id: slug('Weekly sentiment review'),
    name: 'Weekly sentiment review',
    createdAt: createdDaysAgo(6),
    modifiedAt: createdDaysAgo(2),
    author: 'Jane Smith',
    authorBadge: 'gray',
    widgets: [
      { key: 'w1', widget: 'negative-conversation' },
      { key: 'w2', widget: 'nps' },
      { key: 'w3', widget: 'topic-sentiment-split' },
      { key: 'w4', widget: 'competitor-sentiment-comparison' },
      { key: 'w5', widget: 'trending-keywords' },
    ],
  },
  {
    id: slug('Competitive standing'),
    name: 'Competitive standing',
    createdAt: createdDaysAgo(18),
    modifiedAt: createdDaysAgo(5),
    author: 'John Doe',
    authorBadge: 'gray',
    widgets: [
      { key: 'w1', widget: 'share-of-voice-figure' },
      { key: 'w2', widget: 'total-mentions' },
      { key: 'w3', widget: 'share-of-voice' },
      { key: 'w4', widget: 'competitor-sentiment-comparison' },
    ],
  },
  {
    id: slug('Care and complaints'),
    name: 'Care and complaints',
    createdAt: createdDaysAgo(32),
    modifiedAt: createdDaysAgo(21),
    author: 'Harper Lee',
    authorBadge: 'gray',
    widgets: [
      { key: 'w1', widget: 'complaints-by-division' },
      { key: 'w2', widget: 'community-topics' },
      { key: 'w3', widget: 'activity-by-day-and-hour' },
      { key: 'w4', widget: 'mention-volume-over-time' },
    ],
  },
];

export function reportFor(id: string): Report | undefined {
  return reports.find((report) => report.id === id);
}
